package com.evoapps.evoshift.widgets

import kotlinx.datetime.LocalDate

/*
 * SH-166: what a person types into a date field. Polish people write "3.11" or "03.11.2026",
 * others "3/11/2026", "20261103" or the stored ISO form "2026-11-03"; all of them are one date.
 * Days come before months except in the year-first forms (ISO and eight digits).
 */

/** The text of a date field, read leniently. */
sealed interface DateEntry {
    /** Nothing typed. */
    data object Empty : DateEntry

    /** Still being typed ("3.", "2026-11", "2026"): no error while the person types. */
    data object Incomplete : DateEntry

    /** No such day ("31.02", "2026-13-01") or not a date at all. */
    data object Impossible : DateEntry

    data class Complete(val date: LocalDate) : DateEntry
}

/** The year of a date typed without one ("3.11"). */
sealed interface MissingYear {
    /** The current year. */
    data object ThisYear : MissingYear

    /** The current year, or the next one when that day has already passed (deadlines, leave). */
    data object Upcoming : MissingYear

    /** A given year, e.g. the roster's when the field picks days of one month. */
    data class Of(val year: Int) : MissingYear
}

/** The characters a date field takes: digits and the separators below. */
fun isDateCharacter(char: Char): Boolean = char.isDigit() || char in DATE_SEPARATORS

/** "." and "," (the iOS decimal pad in Polish has only a comma), "/" and "-". */
private const val DATE_SEPARATORS = ".,/-"

/** Years a typed date may have: anything else is a typing mistake ("0311" as a year). */
private val YEARS = 1900..2199

/**
 * Reads [input] as a date: "3.11", "03.11", "3.11.2026", "03.11.2026", "3/11/2026", "3,11,2026",
 * "20261103", "03112026" and "2026-11-03". A missing year comes from [missingYear], counted from
 * [today].
 */
fun parseDateEntry(input: String, today: LocalDate, missingYear: MissingYear = MissingYear.ThisYear): DateEntry {
    val text = input.trim()
    if (text.isEmpty()) return DateEntry.Empty
    if (!text.all(::isDateCharacter)) return DateEntry.Impossible
    val parts = text.split('.', ',', '/', '-')
    return when {
        parts.size == 1 -> digitsOnly(text)
        parts.size > 3 -> DateEntry.Impossible
        parts[0].length > 2 -> yearFirst(parts)
        else -> dayFirst(parts, today, missingYear)
    }
}

/** The date [input] stands for, or null while it is not a whole one. */
fun normalisedDate(input: String, today: LocalDate, missingYear: MissingYear = MissingYear.ThisYear): LocalDate? =
    (parseDateEntry(input, today, missingYear) as? DateEntry.Complete)?.date

/** "20261103" (year first) or "03112026" (day first); fewer digits are still being typed. */
private fun digitsOnly(text: String): DateEntry {
    if (text.length < 8) return DateEntry.Incomplete
    if (text.length > 8) return DateEntry.Impossible
    val yearFirst = date(text.take(4).toInt(), text.substring(4, 6).toInt(), text.takeLast(2).toInt())
    val dayFirst = date(text.takeLast(4).toInt(), text.substring(2, 4).toInt(), text.take(2).toInt())
    return (yearFirst ?: dayFirst)?.let { DateEntry.Complete(it) } ?: DateEntry.Impossible
}

/** "2026-11-03" (also "2026-11-3" and "2026.11.03"). */
private fun yearFirst(parts: List<String>): DateEntry {
    if (parts[0].length != 4) return DateEntry.Impossible
    val month = number(parts[1], 12, last = parts.size == 2)
    if (month !is Part.Value) return month.entry
    if (parts.size == 2) return DateEntry.Incomplete
    val day = number(parts[2], 31, last = true)
    if (day !is Part.Value) return day.entry
    return complete(date(parts[0].toInt(), month.value, day.value))
}

/** "3.11", "3.11." and "3.11.2026". */
private fun dayFirst(parts: List<String>, today: LocalDate, missingYear: MissingYear): DateEntry {
    if (parts[0].isEmpty()) return DateEntry.Impossible
    val day = number(parts[0], 31, last = false)
    if (day !is Part.Value) return day.entry
    val month = number(parts[1], 12, last = parts.size == 2)
    if (month !is Part.Value) return month.entry
    val year = parts.getOrNull(2).orEmpty()
    return when {
        year.isEmpty() -> complete(withoutYear(month.value, day.value, today, missingYear))
        year.length < 4 -> DateEntry.Incomplete
        year.length > 4 -> DateEntry.Impossible
        else -> complete(date(year.toInt(), month.value, day.value))
    }
}

private fun withoutYear(month: Int, day: Int, today: LocalDate, missingYear: MissingYear): LocalDate? = when (missingYear) {
    MissingYear.ThisYear -> date(today.year, month, day)
    is MissingYear.Of -> date(missingYear.year, month, day)
    MissingYear.Upcoming -> date(today.year, month, day)?.takeIf { it >= today } ?: date(today.year + 1, month, day)
}

private fun complete(date: LocalDate?): DateEntry = date?.let { DateEntry.Complete(it) } ?: DateEntry.Impossible

private fun date(year: Int, month: Int, day: Int): LocalDate? =
    if (year in YEARS) runCatching { LocalDate(year, month, day) }.getOrNull() else null

/** A day or month part: a value, still being typed (only as the last part), or wrong. */
private sealed interface Part {
    val entry: DateEntry

    data class Value(val value: Int) : Part {
        override val entry: DateEntry get() = DateEntry.Incomplete
    }

    data object Typing : Part {
        override val entry: DateEntry get() = DateEntry.Incomplete
    }

    data object Wrong : Part {
        override val entry: DateEntry get() = DateEntry.Impossible
    }
}

/** [part] as a number 1..[max]; "" or "0" is still being typed when it is the [last] part. */
private fun number(part: String, max: Int, last: Boolean): Part = when {
    part.isEmpty() || part == "0" -> if (last) Part.Typing else Part.Wrong
    part.length > 2 -> Part.Wrong
    else -> part.toInt().let { if (it in 1..max) Part.Value(it) else Part.Wrong }
}
